import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { INR } from "@/lib/constants";
import EODPaymentsPanel from "@/components/EODPaymentsPanel";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { TrendingUp, TrendingDown, Building2, Calculator, ChevronLeft, ChevronRight, Wallet } from "lucide-react";

const VOLT = "#CCFF00";
const months = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const DEPT_COLORS = {
  OG: "#A78BFA",
  STRENGTH: "#CCFF00",
  MMA: "#FF3B30",
  HYROX: "#FBBF24",
  PT: "#7DD3FC",
  SEMI_PT: "#22D3EE",
  CONTRAST: "#F472B6",
  PHYSIO: "#60A5FA",
  MASSAGE: "#FB923C",
  ASSESSMENT: "#A1A1AA",
};

function StatTile({ label, value, accent = "white", sub, testid }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-sm p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">{label}</div>
      <div className="font-display text-4xl font-black mt-2" style={{ color: accent === "volt" ? VOLT : accent === "red" ? "#FF3B30" : accent === "green" ? "#00E676" : accent === "amber" ? "#FBBF24" : accent === "sky" ? "#7DD3FC" : "#fff" }}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function FinancePage() {
  const { user, activeBranchId } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const params = { year, month };
  if (activeBranchId) params.branch_id = activeBranchId;

  const { data } = useQuery({
    queryKey: ["finance-report-v2", year, month, activeBranchId],
    queryFn: async () => (await apiClient.get("/reports/finance", { params })).data,
  });

  const t = data?.totals || {};
  const branches = data?.by_branch || [];
  const constants = data?.constants || {};

  const beneficiaryPie = [
    { name: "Licensor (NAMMA Holding)", value: t.licensor || 0, color: "#A78BFA" },
    { name: "Licensee (Branch)", value: t.licensee || 0, color: VOLT },
    { name: "Coach", value: t.coach || 0, color: "#7DD3FC" },
  ].filter((x) => x.value > 0);

  const deductionsPie = [
    { name: "GST 5%", value: t.gst || 0, color: "#FF3B30" },
    { name: "Razorpay Fee 2%", value: t.razorpay_fee || 0, color: "#FB923C" },
    { name: "Razorpay GST 18%", value: t.razorpay_gst || 0, color: "#FBBF24" },
    { name: "Net Revenue", value: t.net_revenue || 0, color: "#00E676" },
  ].filter((x) => x.value > 0);

  const deptBars = Object.entries(t.by_dept || {}).map(([dept, v]) => ({
    dept,
    Licensor: v.licensor || 0,
    Licensee: v.licensee || 0,
    Coach: v.coach || 0,
  }));

  if (user?.role !== "SUPER_ADMIN" && user?.role !== "ADMIN") {
    return <div className="p-8 text-zinc-400">Forbidden — Super Admin / Admin only.</div>;
  }

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="finance-page">
      {/* End-of-Day payments queue — Super Admin only */}
      <EODPaymentsPanel />

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Finance · Licensor / Licensee / Coach</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Revenue Split & Payouts</h1>
          <div className="text-zinc-500 text-sm mt-1">
            Gross  −  GST 5%  −  Razorpay 2%  −  Razorpay GST 18%  =  <span className="text-volt">Net Revenue</span>  →  carved per department  →  Licensor / Licensee / Coach
          </div>
        </div>
        <div className="flex gap-2">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            data-testid="finance-month-select"
            className="bg-zinc-950 border border-zinc-800 text-white text-sm uppercase tracking-wider px-3 py-2 rounded-sm focus:border-volt outline-none"
          >
            {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            data-testid="finance-year-select"
            className="bg-zinc-950 border border-zinc-800 text-white text-sm uppercase tracking-wider px-3 py-2 rounded-sm focus:border-volt outline-none"
          >
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Daily EOD Settlement Tracker — Super Admin only */}
      {user?.role === "SUPER_ADMIN" && <DailySettlementPanel />}

      {/* Top deduction row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatTile testid="kpi-gross" label="Gross Revenue" value={INR(t.gross)} accent="white" sub={`${data?.payment_count || 0} payments`} />
        <StatTile testid="kpi-gst" label="− GST (5%)" value={INR(t.gst)} accent="red" sub="Service tax to Govt" />
        <StatTile testid="kpi-rzp-fee" label="− Razorpay (2%)" value={INR(t.razorpay_fee)} accent="amber" sub="Payment gateway" />
        <StatTile testid="kpi-rzp-gst" label="− Razorpay GST (18%)" value={INR(t.razorpay_gst)} accent="amber" sub="On the 2% fee" />
        <StatTile testid="kpi-net" label="= Net Revenue" value={INR(t.net_revenue)} accent="volt" sub="Available for distribution" />
      </div>

      {/* Beneficiary row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatTile testid="kpi-licensor" label="Licensor — NAMMA Holding" value={INR(t.licensor)} accent="white" sub={t.net_revenue ? `${((t.licensor / t.net_revenue) * 100).toFixed(1)}% of Net` : "—"} />
        <StatTile testid="kpi-licensee" label="Licensee — Branch / Partner" value={INR(t.licensee)} accent="volt" sub={t.net_revenue ? `${((t.licensee / t.net_revenue) * 100).toFixed(1)}% of Net` : "—"} />
        <StatTile testid="kpi-coach" label="Coach Payouts" value={INR(t.coach)} accent="sky" sub={t.net_revenue ? `${((t.coach / t.net_revenue) * 100).toFixed(1)}% of Net` : "—"} />
      </div>

      {/* Two pies */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel>
          <PanelHeader title="Net Revenue → Beneficiaries" subtitle="Where every rupee of Net lands" />
          {beneficiaryPie.length === 0 ? (
            <div className="text-zinc-500 text-sm">No revenue in this period.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={beneficiaryPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={100} paddingAngle={2}>
                  {beneficiaryPie.map((e, i) => <Cell key={i} fill={e.color} stroke="#0a0a0a" />)}
                </Pie>
                <Tooltip formatter={(v) => INR(v)} contentStyle={{ background: "#0c0c0c", border: "1px solid #27272a", borderRadius: 4 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Panel>
        <Panel>
          <PanelHeader title="Gross → Deduction Waterfall" subtitle="What leaves before distribution" />
          {deductionsPie.length === 0 ? (
            <div className="text-zinc-500 text-sm">No revenue in this period.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={deductionsPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={100} paddingAngle={2}>
                  {deductionsPie.map((e, i) => <Cell key={i} fill={e.color} stroke="#0a0a0a" />)}
                </Pie>
                <Tooltip formatter={(v) => INR(v)} contentStyle={{ background: "#0c0c0c", border: "1px solid #27272a", borderRadius: 4 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      {/* Per department breakdown */}
      <Panel>
        <PanelHeader title="Department-wise Net Allocation" subtitle="Per the official department-share matrix" />
        {deptBars.length === 0 ? (
          <div className="text-zinc-500 text-sm">No revenue this month.</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={deptBars} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
              <CartesianGrid stroke="#1f1f23" vertical={false} />
              <XAxis dataKey="dept" stroke="#52525B" fontSize={10} />
              <YAxis stroke="#52525B" fontSize={10} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => INR(v)} contentStyle={{ background: "#0c0c0c", border: "1px solid #27272a", borderRadius: 4 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Licensor" stackId="a" fill="#A78BFA" />
              <Bar dataKey="Licensee" stackId="a" fill={VOLT} />
              <Bar dataKey="Coach" stackId="a" fill="#7DD3FC" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Panel>

      {/* Per branch — Licensee vs target */}
      <Panel>
        <PanelHeader
          title="Licensee Share · Actual vs Monthly Target"
          subtitle="Branch operating-cost target (rent + maintenance + opex): Cunningham ₹2.5L · RMV ₹2.0L · Jayanagar ₹2.0L"
        />
        {branches.length === 0 ? (
          <div className="text-zinc-500 text-sm">No revenue this month — nothing to allocate.</div>
        ) : (
          <div className="space-y-3" data-testid="branch-table">
            {branches.map((b) => {
              const pct = b.licensee_coverage_pct ?? 0;
              const color = pct >= 100 ? "#00E676" : pct >= 60 ? VOLT : "#FF3B30";
              const variance = b.licensee_variance;
              return (
                <div key={b.branch_id} className="bg-zinc-950 border border-zinc-900 rounded-sm p-4" data-testid={`branch-row-${b.branch_id}`}>
                  <div className="flex items-baseline justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Building2 size={16} className="text-zinc-500" />
                      <div className="font-display text-xl font-bold uppercase tracking-tight">{b.branch_name}</div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div>
                        <span className="text-zinc-500 text-[10px] uppercase tracking-[0.16em]">Licensee Share</span>
                        <span className="ml-2 font-display text-2xl font-black" style={{ color }}>{INR(b.licensee)}</span>
                      </div>
                      <span className="text-zinc-700">/</span>
                      <div>
                        <span className="text-zinc-500 text-[10px] uppercase tracking-[0.16em]">Target</span>
                        <span className="ml-2 font-display text-xl font-bold text-zinc-300">{b.operating_target ? INR(b.operating_target) : "—"}</span>
                      </div>
                      {variance !== null && variance !== undefined && (
                        <div className="flex items-center gap-1 ml-2 text-xs">
                          {variance >= 0 ? <TrendingUp size={14} color="#00E676" /> : <TrendingDown size={14} color="#FF3B30" />}
                          <span style={{ color: variance >= 0 ? "#00E676" : "#FF3B30" }}>
                            {variance >= 0 ? "+" : ""}{INR(variance)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="h-2 mt-3 bg-zinc-900 rounded-sm overflow-hidden">
                    <div className="h-full" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
                  </div>
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.16em] text-zinc-500 mt-2 flex-wrap gap-2">
                    <span>{pct}% covered</span>
                    <div className="flex gap-4 flex-wrap">
                      <span>Gross {INR(b.gross)}</span>
                      <span>Net {INR(b.net_revenue)}</span>
                      <span style={{ color: "#A78BFA" }}>Licensor {INR(b.licensor)}</span>
                      <span style={{ color: "#7DD3FC" }}>Coach {INR(b.coach)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {/* Formula explainer */}
      <Panel>
        <PanelHeader title="Split Algorithm" subtitle="Spec — matches the official pricing & share matrix" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-zinc-300">
          <div>
            <div className="text-volt text-[10px] uppercase tracking-[0.22em] font-bold mb-2 flex items-center gap-2">
              <Calculator size={12} /> Step 1 — Compute Net Revenue
            </div>
            <pre className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 text-xs leading-relaxed text-zinc-300 overflow-x-auto font-mono">
{`GST          = Gross × 5%             → Govt
Razorpay Fee = Gross × 2%             → Razorpay
Razorpay GST = Razorpay Fee × 18%     → Govt (via RZP)
Net Revenue  = Gross − GST − RZP Fee − RZP GST
             ≈ Gross × 92.64%`}
            </pre>
          </div>
          <div>
            <div className="text-volt text-[10px] uppercase tracking-[0.22em] font-bold mb-2 flex items-center gap-2">
              <Calculator size={12} /> Step 2 — Carve into Departments
            </div>
            <pre className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 text-xs leading-relaxed text-zinc-300 overflow-x-auto font-mono">
{`Open Gym Monthly (₹6k) → 100% OG
Strength Monthly (₹8.5k) → 50% OG + 50% Strength
MMA Monthly (₹7k)        → 60% OG + 40% MMA
All-Access (₹12k)        → 30% OG + 30% Str + 20% MMA + 20% Hyrox
PT 12-pack (₹14k)        → 100% PT`}
            </pre>
          </div>
        </div>
        <div className="mt-6">
          <div className="text-volt text-[10px] uppercase tracking-[0.22em] font-bold mb-2 flex items-center gap-2">
            <Calculator size={12} /> Step 3 — Apply Department Share Rules
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-zinc-300">
              <thead className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 border-b border-zinc-900">
                <tr>
                  <th className="text-left p-2">Department</th>
                  <th className="text-right p-2">Licensor</th>
                  <th className="text-right p-2">Licensee</th>
                  <th className="text-right p-2">Coach</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Open Gym (OG)", "50%", "50%", "0%"],
                  ["Strength / MMA / Hyrox", "10%", "60% (after coach)", "33.3% of Licensee = 30% of Net"],
                  ["PT / Semi-PT", "25%", "25%", "50%"],
                  ["Contrast", "50%", "50%", "0%"],
                  ["Physio / Massage / Assessments", "33.33%", "33.33%", "33.33%"],
                ].map(([d, lr, le, co]) => (
                  <tr key={d} className="border-b border-zinc-900/50">
                    <td className="p-2 font-semibold">{d}</td>
                    <td className="p-2 text-right" style={{ color: "#A78BFA" }}>{lr}</td>
                    <td className="p-2 text-right" style={{ color: VOLT }}>{le}</td>
                    <td className="p-2 text-right" style={{ color: "#7DD3FC" }}>{co}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Panel>
    </div>
  );
}


function DailySettlementPanel() {
  const [dateObj, setDateObj] = useState(new Date());
  const dateStr = dateObj.toISOString().slice(0, 10);
  const { data } = useQuery({
    queryKey: ["daily-settlement", dateStr],
    queryFn: async () => (await apiClient.get("/reports/finance/daily-settlement", { params: { day: dateStr } })).data,
  });
  const isToday = dateStr === new Date().toISOString().slice(0, 10);
  const shift = (n) => setDateObj(new Date(dateObj.getTime() + n * 86400000));

  return (
    <Panel data-testid="daily-settlement-panel">
      <PanelHeader
        title="End-of-Day Settlement"
        subtitle="Daily tracker — GST payable + Licensee payouts settled at close of business"
      />
      <div className="flex items-center gap-2 mb-4" data-testid="daily-date-nav">
        <Button variant="ghost" size="sm" onClick={() => shift(-1)} data-testid="daily-prev"><ChevronLeft size={14} /></Button>
        <div className="bg-black border border-zinc-800 rounded-sm px-3 py-1.5 flex items-center gap-2">
          <Wallet size={14} className="text-volt" />
          <input type="date" value={dateStr} onChange={(e) => setDateObj(new Date(e.target.value))}
            data-testid="daily-date-input" className="bg-transparent text-white text-sm outline-none font-mono" />
        </div>
        <Button variant="ghost" size="sm" onClick={() => shift(1)} data-testid="daily-next"><ChevronRight size={14} /></Button>
        {!isToday && (
          <Button variant="outline" size="sm" onClick={() => setDateObj(new Date())} data-testid="daily-today" className="rounded-sm">Today</Button>
        )}
        <div className="ml-auto text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          {data?.payment_count || 0} payments
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-black border border-zinc-900 rounded-sm p-4" data-testid="tile-gross-today">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Gross Today</div>
          <div className="font-display text-4xl font-black text-white mt-2">{INR(data?.gross)}</div>
          <div className="text-[10px] text-zinc-500 mt-1">Revenue collected</div>
        </div>
        <div className="bg-red-500/5 border border-red-500/30 rounded-sm p-4" data-testid="tile-gst-due">
          <div className="text-[10px] uppercase tracking-[0.22em] text-red-400 font-semibold">GST to Pay</div>
          <div className="font-display text-4xl font-black text-red-400 mt-2">{INR(data?.gst_to_pay)}</div>
          <div className="text-[10px] text-zinc-500 mt-1">5% of gross → Govt</div>
        </div>
        <div className="bg-volt/5 border border-volt/30 rounded-sm p-4" data-testid="tile-licensee-due">
          <div className="text-[10px] uppercase tracking-[0.22em] text-volt font-semibold">Licensee Payout Due</div>
          <div className="font-display text-4xl font-black text-volt mt-2">{INR(data?.licensee_payout_due)}</div>
          <div className="text-[10px] text-zinc-500 mt-1">Settle to branches at EOD</div>
        </div>
        <div className="bg-sky-500/5 border border-sky-500/30 rounded-sm p-4" data-testid="tile-coach-due">
          <div className="text-[10px] uppercase tracking-[0.22em] text-sky-400 font-semibold">Coach Payout Due</div>
          <div className="font-display text-4xl font-black text-sky-400 mt-2">{INR(data?.coach_payout_due)}</div>
          <div className="text-[10px] text-zinc-500 mt-1">From today&apos;s earnings</div>
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold mb-2">By Branch · settlement instructions</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="daily-branch-table">
            <thead className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 border-b border-zinc-900">
              <tr>
                <th className="text-left py-2 px-2 font-semibold">Branch</th>
                <th className="text-right py-2 px-2 font-semibold">Orders</th>
                <th className="text-right py-2 px-2 font-semibold">Gross</th>
                <th className="text-right py-2 px-2 font-semibold text-volt">Licensee</th>
                <th className="text-right py-2 px-2 font-semibold text-sky-400">Coach</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_branch || []).map((b) => (
                <tr key={b.branch_id} data-testid={`daily-branch-row-${b.branch_id}`} className="border-b border-zinc-900">
                  <td className="py-2 px-2 flex items-center gap-2"><Building2 size={12} className="text-zinc-500" /><span className="font-semibold">{b.branch_name}</span></td>
                  <td className="py-2 px-2 text-right text-zinc-400">{b.payment_count}</td>
                  <td className="py-2 px-2 text-right text-zinc-300">{INR(b.gross)}</td>
                  <td className="py-2 px-2 text-right font-display font-bold text-volt">{INR(b.licensee_payout_due)}</td>
                  <td className="py-2 px-2 text-right font-display font-bold text-sky-400">{INR(b.coach_payout_due)}</td>
                </tr>
              ))}
              {(!data?.by_branch || data.by_branch.length === 0) && (
                <tr><td colSpan={5} className="py-4 text-center text-zinc-600 text-sm">No settlement due today.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3 text-[10px] uppercase tracking-[0.16em] text-zinc-600">
        Formula: Gross → −5% GST → −2% Razorpay Fee (+18% GST on it) → Net → carve by dept → Licensor / Licensee / Coach
      </div>
    </Panel>
  );
}
